/**
 * layouts.js — Carousel Composer layout registry: every slide shape beyond the
 * five quick styles on the slide panel.
 *
 * A layout is declarative. It owns nothing but its rectangles:
 *
 *   { id, name, collection, cells, describe(), rects(W, H, design, slide) }
 *
 * geometry.slideCells() asks the registry whenever a slide names one of these
 * ids, so the preview and the export keep drawing from the same source of truth
 * — nothing here knows about canvases, the DOM or the store.
 *
 * TWO THINGS THE FIVE QUICK STYLES COULD NOT SAY:
 *
 *   MAT     a rectangle inset from the slide edge, asymmetrically, so the design's
 *           fill colour reads as a mount around the print. That is just a smaller
 *           rectangle — it needs no renderer support at all.
 *   OVERLAP two rectangles that intersect. Rects come back in draw order, so a
 *           later one sits over an earlier one in the preview (DOM order) and in
 *           the export (draw order) alike. Such a rect may also carry `mat`: the
 *           px of fill colour to paint AROUND it before its photo, so a small
 *           frame reads as a print laid on a print rather than a collage.
 *
 * `mat` is advisory. The rectangle itself is authoritative — the crop, the drag
 * and the export all use it whether or not the fill ring is painted — so a
 * renderer that ignores `mat` simply shows the overlap with no white edge.
 *
 * MARGINS ARE FRACTIONS of the slide, never pixels: one layout then looks the
 * same at 1080, 1440 and 2160, and at any format. They are tuned here, per
 * layout, and are deliberately not exposed as controls. The design's own pad and
 * gap still apply on top: pad insets the whole layout, gap separates the cells
 * that sit side by side.
 */

export const COLLECTIONS = [
  { key: 'clean', name: 'Clean', note: 'The quick styles' },
  { key: 'editorial', name: 'Editorial', note: 'Mats and overlaps' },
  { key: 'grids', name: 'Grids', note: 'Even divisions' },
  { key: 'spreads', name: 'Spreads', note: 'One photo across two slides' },
];

/* ---------------- helpers ---------------- */

// The area a layout works inside: the slide less the design's border.
function innerBox(W, H, design) {
  const pad = Math.max(0, (design && design.pad) || 0);
  const gap = Math.max(0, (design && design.gap) || 0);
  return { x: pad, y: pad, w: Math.max(2, W - 2 * pad), h: Math.max(2, H - 2 * pad), pad, gap };
}

// A rectangle inset from `b` by four margins, in px.
function inset(b, l, t, r, btm) {
  return { x: b.x + l, y: b.y + t, w: Math.max(1, b.w - l - r), h: Math.max(1, b.h - t - btm) };
}

const rect = (x, y, w, h, mat) => (mat ? { x, y, w, h, mat } : { x, y, w, h });

/**
 * Does anything paint the ring yet? Yes, since 2026-09-22: SlideStage.jsx lays a
 * sibling `.cc-mat` under each matted cell and renderSlideToBlob fills the same
 * grown rectangle before drawing it, so the gallery may show the edge too.
 *
 * Kept as a flag rather than deleted because it is what says the three renderers
 * are in step: a fourth surface that draws a slide sets it false until it is.
 */
export const MATS_PAINTED = true;

/* ---------------- the layouts ---------------- */

const EDITORIAL = [
  {
    id: 'mat-wide',
    name: 'Wide Mat',
    collection: 'editorial',
    cells: 1,
    roles: [''],
    roleNames: ['photo'],
    describe: () => 'One photo on a deep mount, weighted to the foot.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      // the foot is deeper than the head, the way a mounted print is cut
      return [inset(b, 0.085 * b.w, 0.075 * b.h, 0.085 * b.w, 0.2 * b.h)];
    },
  },
  {
    id: 'inset-corner',
    name: 'Corner Inset',
    collection: 'editorial',
    cells: 2,
    roles: ['under', 'inset'],
    roleNames: ['large photo', 'inset photo'],
    describe: () => 'A small frame laid over the corner of a large one.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const m = 0.055 * b.w;
      const big = inset(b, m, m, m, m);
      const sw = 0.42 * b.w, sh = 0.32 * b.h;
      // it overhangs the large frame's corner, so part of it lands on the fill
      const x = b.x + b.w - sw - 0.018 * b.w;
      const y = b.y + b.h - sh - 0.018 * b.h;
      return [big, rect(x, y, sw, sh, 0.022 * Math.min(b.w, b.h))];
    },
  },
  {
    id: 'inset-twin',
    name: 'Twin Inset',
    collection: 'editorial',
    cells: 3,
    roles: ['under', 'upper', 'lower'],
    roleNames: ['large photo', 'upper inset', 'lower inset'],
    describe: () => 'Two small frames stepped down a large one.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const m = 0.055 * b.w;
      const big = { x: b.x + m, y: b.y + m, w: 0.66 * b.w, h: b.h - 2 * m };
      const sw = 0.36 * b.w, sh = 0.26 * b.h;
      const mat = 0.02 * Math.min(b.w, b.h);
      const x1 = b.x + b.w - sw - 0.02 * b.w;
      // the pair sits centred on the large frame, so the fill above and below it balances
      const y1 = b.y + 0.165 * b.h;
      // the second is a step further down AND back, so the two read as a stagger
      return [big, rect(x1, y1, sw, sh, mat), rect(x1 - 0.035 * b.w, y1 + sh + 0.06 * b.h, sw, sh, mat)];
    },
  },
  {
    id: 'portrait-offset',
    name: 'Offset Portrait',
    collection: 'editorial',
    cells: 1,
    roles: [''],
    roleNames: ['tall photo'],
    describe: () => 'A tall photo pushed to one side, wide margin beside it.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const w = 0.58 * b.w;
      return [rect(b.x + b.w - w - 0.075 * b.w, b.y + 0.085 * b.h, w, 0.77 * b.h)];
    },
  },
];

const GRIDS = [
  {
    id: 'grid-tall-two',
    name: 'Tall + Two',
    collection: 'grids',
    cells: 3,
    roles: ['tall', 'upper', 'lower'],
    roleNames: ['tall photo', 'upper right photo', 'lower right photo'],
    describe: () => 'A tall photo beside two stacked ones.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const colW = Math.max(1, (b.w - b.gap) * 0.56);
      const restW = Math.max(1, b.w - b.gap - colW);
      const rowH = Math.max(1, (b.h - b.gap) / 2);
      const x2 = b.x + colW + b.gap;
      return [
        rect(b.x, b.y, colW, b.h),
        rect(x2, b.y, restW, rowH),
        rect(x2, b.y + rowH + b.gap, restW, rowH),
      ];
    },
  },
  {
    id: 'grid-three',
    name: 'Three Up',
    collection: 'grids',
    cells: 3,
    roles: ['top', 'middle', 'bottom'],
    roleNames: ['top photo', 'middle photo', 'bottom photo'],
    describe: () => 'Three full-width bands.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const h = Math.max(1, (b.h - 2 * b.gap) / 3);
      return [0, 1, 2].map((i) => rect(b.x, b.y + i * (h + b.gap), b.w, h));
    },
  },
  {
    id: 'grid-quarter',
    name: 'Quarter Grid',
    collection: 'grids',
    cells: 4,
    roles: ['top left', 'top right', 'btm left', 'btm right'],
    roleNames: ['top left photo', 'top right photo', 'bottom left photo', 'bottom right photo'],
    describe: () => 'Four equal quarters.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const w = Math.max(1, (b.w - b.gap) / 2);
      const h = Math.max(1, (b.h - b.gap) / 2);
      const x2 = b.x + w + b.gap, y2 = b.y + h + b.gap;
      return [rect(b.x, b.y, w, h), rect(x2, b.y, w, h), rect(b.x, y2, w, h), rect(x2, y2, w, h)];
    },
  },
];

/**
 * SPREADS run one photo across several slides. They are panorama groups — the
 * same machinery, the same seam expression — except that a spread part honours
 * the design's border and gap instead of going full bleed, so the mount carries
 * across the seam. Every part gets the SAME rectangle, which is what keeps the
 * strips tiling: the crop is derived from the cell's aspect, so a part that was
 * shaped differently from its neighbours would not meet it.
 */
const SPREADS = [
  {
    id: 'spread-mat',
    name: 'Matted Spread',
    collection: 'spreads',
    cells: 1,
    parts: 2,
    spread: true,
    roles: [''],
    roleNames: ['spread photo'],
    describe: () => 'One photo across two slides, mount and all.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const m = 0.05 * Math.min(W, H);
      // half the gap on each side of the seam, so the design's gap is what you see between the parts
      const x = b.pad + m + b.gap / 2;
      const y = b.pad + m;
      return [rect(x, y, Math.max(1, W - 2 * x), Math.max(1, H - 2 * y))];
    },
  },
  {
    id: 'spread-split',
    name: 'Split Hero',
    collection: 'spreads',
    cells: 2,
    parts: 2,
    spread: true,
    roles: ['spread', 'below'],
    roleNames: ['spread photo', 'photo below'],
    describe: () => 'A band across two slides, its own photo under each half.',
    rects(W, H, design) {
      const b = innerBox(W, H, design);
      const x = b.pad + b.gap / 2;
      const w = Math.max(1, W - 2 * x);
      const band = Math.max(1, (b.h - b.gap) * 0.6);
      const y2 = b.y + band + b.gap;
      return [rect(x, b.y, w, band), rect(x, y2, w, Math.max(1, b.y + b.h - y2))];
    },
  },
];

/**
 * The four quick styles, described for the gallery only. They have no rects() —
 * geometry.slideCells() draws them exactly as it always has, and getLayout()
 * deliberately does not return them, so nothing can route a built-in through the
 * registry by accident.
 */
export const CLEAN = [
  { id: 'single', name: 'Single', collection: 'clean', cells: 1, describe: () => 'One photo, edge to edge.' },
  { id: 'stack', name: 'Stacked', collection: 'clean', cells: 2, describe: () => 'Two photos, one above the other.' },
  { id: 'row', name: 'Side by Side', collection: 'clean', cells: 2, describe: () => 'Two photos, side by side.' },
  { id: 'hero3', name: 'Hero + 3', collection: 'clean', cells: 4, describe: () => 'A hero across the top, three beneath.' },
];

export const LAYOUTS = [...EDITORIAL, ...GRIDS, ...SPREADS];

const BY_ID = new Map(LAYOUTS.map((l) => [l.id, l]));

/**
 * Layouts that exist but are not offered.
 *
 * DEFERRED 2026-09-22 on the user's steer — "let's not do panorama runs side by
 * side", pending a pick from Swirl Studies. A Matted Spread IS a panorama run and
 * its gallery tile draws the parts side by side, so both entries leave the
 * catalog. Everything they need stays and stays tested — isSpreadLayout(),
 * slideGeom()'s pad/gap for a spread, makePano()'s layout argument, setLayout()'s
 * keep-the-group branch — and a saved carousel that already holds one still
 * reopens. Emptying this set is the whole of putting them back.
 */
export const DEFERRED = new Set(['spread-mat', 'spread-split']);

/** A registry layout, or null for the quick styles and anything unknown. */
export function getLayout(id) {
  return BY_ID.get(id) || null;
}

/** Spreads run across slides and keep their panorama group when the layout is set. */
export function isSpreadLayout(id) {
  const l = getLayout(id);
  return !!(l && l.spread);
}

/** { id: cells } for every registry layout — geometry folds this into CELLS. */
export function layoutCellCounts() {
  return Object.fromEntries(LAYOUTS.map((l) => [l.id, l.cells]));
}

/** Everything the gallery lists: the quick styles first, then the collections. */
export function layoutCatalog() {
  return [...CLEAN, ...LAYOUTS].filter((l) => !DEFERRED.has(l.id));
}

/** The catalog grouped in collection order, empty collections dropped. */
export function layoutsByCollection() {
  const all = layoutCatalog();
  return COLLECTIONS
    .map((c) => ({ ...c, items: all.filter((l) => l.collection === c.key) }))
    .filter((c) => c.items.length);
}
